import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  getUniboxThreads,
  getUniboxThread,
  sendUniboxMessage,
  setUniboxThreadState,
  getUniboxAutomation,
  draftUniboxReply,
  UniboxThread,
  UniboxThreadDetail,
  UniboxMessage,
} from "../../../database/community/unibox";
import UniboxAutomations from "./UniboxAutomations";

// Unified social unibox tab for the Messaging screen. IG/FB/WhatsApp DMs,
// comments, and LinkedIn threads render here; reply inline.
// Reads/sends via callables only (the store is server-only). Triage model:
// open threads are the queue, replying or marking done clears them, snoozed
// threads wake back up into Open when the snooze expires.

const Wrap = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: var(--space-3);
  min-height: 420px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Col = styled.div`
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ColHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const ThreadItem = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  border-bottom: 1px solid var(--color-border-subtle);
  background: ${(p) => (p.$active ? "var(--color-surface-subtle)" : "transparent")};
  text-align: left;
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;

  &:hover {
    background: var(--color-surface-subtle);
  }
`;

const ThreadTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Name = styled.span`
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Snippet = styled.span`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tag = styled.span`
  font-family: var(--font-sans);
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 1px 5px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-subtle);
  color: var(--color-text-secondary);
`;

const TimeLabel = styled.span`
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--color-text-secondary);
  margin-left: auto;
  flex-shrink: 0;
`;

const UnreadDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-accent);
  flex-shrink: 0;
`;

const Messages = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Bubble = styled.div<{ $out: boolean; $pending?: boolean; $failed?: boolean }>`
  align-self: ${(p) => (p.$out ? "flex-end" : "flex-start")};
  max-width: 78%;
  padding: 6px 10px;
  border-radius: 12px;
  font-family: var(--font-sans);
  font-size: 12px;
  line-height: 1.4;
  background: ${(p) => (p.$out ? "var(--color-accent)" : "var(--color-surface-subtle)")};
  color: ${(p) => (p.$out ? "#fff" : "var(--color-text)")};
  white-space: pre-wrap;
  word-break: break-word;
  opacity: ${(p) => (p.$pending ? 0.6 : 1)};
  ${(p) => (p.$failed ? "outline: 1px solid var(--color-danger);" : "")}
`;

const BubbleMeta = styled.span<{ $out: boolean }>`
  align-self: ${(p) => (p.$out ? "flex-end" : "flex-start")};
  font-family: var(--font-sans);
  font-size: 9px;
  color: var(--color-text-secondary);
  margin-top: -3px;
`;

const DaySep = styled.div`
  align-self: center;
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--color-text-secondary);
  padding: 2px 10px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-subtle);
  margin: 6px 0 2px;
`;

const Composer = styled.div`
  display: flex;
  gap: 6px;
  padding: var(--space-2) var(--space-3);
  border-top: 1px solid var(--color-border-subtle);
  align-items: center;
`;

const PostLink = styled.a`
  font-family: var(--font-sans);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 1px 6px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-subtle);
  color: var(--color-accent);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SnippetTray = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: var(--space-2) var(--space-3) 0;
`;

const SnippetChip = styled.button`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface-subtle);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: 11px;
  padding: 3px 9px;
  border-radius: var(--radius-pill);
  cursor: pointer;

  &:hover {
    color: var(--color-text);
    border-color: var(--color-accent);
  }
`;

const Input = styled.input`
  flex: 1 1 auto;
  font-family: var(--font-sans);
  font-size: 12px;
  padding: 7px 10px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
`;

const Button = styled.button`
  appearance: none;
  border: 1px solid var(--color-accent);
  background: var(--color-accent);
  color: #fff;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  padding: 6px 14px;
  border-radius: var(--radius-pill);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  background: transparent;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: 10px;
  padding: 3px 10px;
  border-radius: var(--radius-pill);
  cursor: pointer;

  &:hover {
    color: var(--color-text);
    border-color: var(--color-border);
  }
`;

const Muted = styled.div`
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--color-text-secondary);
  padding: var(--space-4);
  text-align: center;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: 1px solid ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-border-subtle)")};
  background: transparent;
  color: ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-text-secondary)")};
  font-family: var(--font-sans);
  font-size: 10px;
  padding: 2px 8px;
  border-radius: var(--radius-pill);
  cursor: pointer;
`;

const FilterGroup = styled.div`
  display: inline-flex;
  gap: 4px;
  align-items: center;
  padding-right: 10px;
  margin-right: 6px;
  border-right: 1px solid var(--color-border-subtle);

  &:last-child {
    border-right: none;
  }
`;

const SearchInput = styled.input`
  font-family: var(--font-sans);
  font-size: 11px;
  padding: 3px 10px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  width: 160px;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const LoadMoreButton = styled.button`
  appearance: none;
  border: none;
  border-top: 1px solid var(--color-border-subtle);
  background: transparent;
  color: var(--color-accent);
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  padding: var(--space-2);
  cursor: pointer;
  width: 100%;

  &:hover {
    background: var(--color-surface-subtle);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const WindowChip = styled.span<{ $closed: boolean }>`
  font-family: var(--font-sans);
  font-size: 9px;
  padding: 2px 8px;
  border-radius: var(--radius-pill);
  white-space: nowrap;
  background: ${(p) => (p.$closed ? "var(--color-danger)" : "var(--color-surface-subtle)")};
  color: ${(p) => (p.$closed ? "#fff" : "var(--color-text-secondary)")};
`;

const threadName = (t: { participant: UniboxThread["participant"] }): string =>
  t.participant.name ||
  (t.participant.handle ? `@${t.participant.handle}` : t.participant.id || "Unknown");

const relativeTime = (ms: number): string => {
  if (!ms) return "";
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(ms).toLocaleDateString();
};

const clockTime = (ms: number): string =>
  new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const hoursLeft = (deadline: number): number =>
  Math.max(0, Math.ceil((deadline - Date.now()) / 3600000));

type PlatformFilter = "all" | "instagram" | "facebook" | "linkedin" | "whatsapp";
type StateFilter = "open" | "snoozed" | "done";
type SortMode = "newest" | "oldestWaiting";

// Local message shape: server messages plus optimistic pending/failed sends.
type LocalMessage = UniboxMessage & { pending?: boolean; failed?: boolean };

const SNOOZE_OPTIONS: { label: string; ms: number }[] = [
  { label: "1h", ms: 3600000 },
  { label: "1d", ms: 86400000 },
  { label: "3d", ms: 3 * 86400000 },
];

export const UniboxPanel: React.FC<{ communityId: string }> = ({ communityId }) => {
  const [threads, setThreads] = useState<UniboxThread[]>([]);
  const [platform, setPlatform] = useState<PlatformFilter>("all");
  const [stateFilter, setStateFilter] = useState<StateFilter>("open");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [sort, setSort] = useState<SortMode>("newest");
  const [search, setSearch] = useState("");
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // True once the operator has paged past the first page; pauses the 30s poll
  // so a background refresh does not collapse a deep-scrolled list.
  const [pagedBeyond, setPagedBeyond] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [detail, setDetail] = useState<UniboxThreadDetail | null>(null);
  const [localMessages, setLocalMessages] = useState<LocalMessage[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [showAutomations, setShowAutomations] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [snippets, setSnippets] = useState<string[]>([]);
  const [showSnippets, setShowSnippets] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);

  const draft = drafts[selectedId] || "";
  const setDraft = (text: string) =>
    setDrafts((prev) => ({ ...prev, [selectedId]: text }));

  const queryFilters = useCallback(
    () => ({
      communityId,
      platform: platform === "all" ? undefined : platform,
      state: stateFilter,
      unreadOnly: unreadOnly || undefined,
      sort,
      search: search.trim() || undefined,
      limit: 40,
    }),
    [communityId, platform, stateFilter, unreadOnly, sort, search]
  );

  const refreshThreads = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError("");
    try {
      const res = await getUniboxThreads(queryFilters());
      setThreads(res.threads || []);
      setNextCursor(res.nextCursor || null);
      setPagedBeyond(false);
    } catch (e: any) {
      setError(e?.message || "Could not load the inbox.");
    } finally {
      setLoading(false);
    }
  }, [communityId, queryFilters]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError("");
    try {
      const res = await getUniboxThreads({ ...queryFilters(), cursor: nextCursor });
      setThreads((prev) => {
        // Dedup on id so a cursor overlap never doubles a thread.
        const seen = new Set(prev.map((t) => t.id));
        return [...prev, ...(res.threads || []).filter((t) => !seen.has(t.id))];
      });
      setNextCursor(res.nextCursor || null);
      setPagedBeyond(true);
    } catch (e: any) {
      setError(e?.message || "Could not load more threads.");
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, queryFilters]);

  // Load on filter/search change; debounce only while typing a search.
  useEffect(() => {
    const t = setTimeout(refreshThreads, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [refreshThreads, search]);

  // Background freshness poll — first page only, so it never discards the
  // extra pages an operator has scrolled into view.
  useEffect(() => {
    const id = setInterval(() => {
      if (!pagedBeyond) refreshThreads();
    }, 30000);
    return () => clearInterval(id);
  }, [refreshThreads, pagedBeyond]);

  // Saved snippets (canned replies) for the composer quick-pick.
  useEffect(() => {
    if (!communityId) return;
    let cancelled = false;
    getUniboxAutomation({ communityId })
      .then((r) => {
        if (!cancelled) setSnippets(r.cannedReplies || []);
      })
      .catch(() => {
        /* best-effort; snippets are optional */
      });
    return () => {
      cancelled = true;
    };
  }, [communityId]);

  const openThread = useCallback(
    async (threadId: string) => {
      setSelectedId(threadId);
      setDetail(null);
      setLocalMessages([]);
      setError("");
      try {
        const d = await getUniboxThread({ communityId, threadId });
        setDetail(d);
        setLocalMessages(d.messages);
        await setUniboxThreadState({ communityId, threadId, markRead: true });
        setThreads((prev) =>
          prev.map((t) => (t.id === threadId ? { ...t, unreadCount: 0 } : t))
        );
      } catch (e: any) {
        setError(e?.message || "Could not open the thread.");
      }
    },
    [communityId]
  );

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [localMessages.length, detail]);

  const changeState = async (state: StateFilter, snoozeMs?: number) => {
    if (!detail) return;
    const threadId = detail.id;
    try {
      await setUniboxThreadState({
        communityId,
        threadId,
        state,
        snoozeUntil: snoozeMs ? Date.now() + snoozeMs : undefined,
      });
      // The thread leaves the current view; clear the pane.
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
      setSelectedId("");
      setDetail(null);
      setLocalMessages([]);
    } catch (e: any) {
      setError(e?.message || "Could not update the thread.");
    }
  };

  const sendText = async (text: string, tempId: string) => {
    if (!detail) return;
    setSending(true);
    setError("");
    try {
      await sendUniboxMessage({ communityId, threadId: detail.id, text });
      setLocalMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, pending: false, failed: false } : m))
      );
      refreshThreads();
    } catch (e: any) {
      setLocalMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, pending: false, failed: true } : m))
      );
      setError(e?.message || "Could not send the reply.");
    } finally {
      setSending(false);
    }
  };

  const handleSend = () => {
    const text = draft.trim();
    if (!text || !detail || sending) return;
    const tempId = `local:${Date.now()}`;
    setLocalMessages((prev) => [
      ...prev,
      { id: tempId, direction: "out", text, at: Date.now(), pending: true },
    ]);
    setDraft("");
    sendText(text, tempId);
  };

  const retrySend = (m: LocalMessage) => {
    if (sending) return;
    setLocalMessages((prev) =>
      prev.map((x) => (x.id === m.id ? { ...x, pending: true, failed: false } : x))
    );
    sendText(m.text, m.id);
  };

  const handleDraft = async () => {
    if (!detail || drafting) return;
    setDrafting(true);
    setError("");
    try {
      const { draft: suggestion } = await draftUniboxReply({
        communityId,
        threadId: detail.id,
      });
      // Draft-first: fill the composer, never auto-send. Append if the operator
      // already started typing.
      setDraft(draft.trim() ? `${draft.trim()} ${suggestion}` : suggestion);
    } catch (e: any) {
      setError(e?.message || "Could not draft a reply.");
    } finally {
      setDrafting(false);
    }
  };

  const insertSnippet = (text: string) => {
    setDraft(draft.trim() ? `${draft.trim()} ${text}` : text);
    setShowSnippets(false);
  };

  const windowClosed = Boolean(
    detail?.windowExpiresAt && Date.now() > detail.windowExpiresAt
  );
  const canReply = Boolean(detail) && !windowClosed;

  const composerPlaceholder = windowClosed
    ? "Messaging window closed. The contact must message first."
    : "Write a reply…";

  let lastDay = "";

  return (
    <div>
      <div style={{ display: "flex", gap: 0, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <FilterGroup>
          {(["all", "instagram", "facebook", "linkedin", "whatsapp"] as PlatformFilter[]).map((p) => (
            <FilterButton key={p} type="button" $active={platform === p} onClick={() => setPlatform(p)}>
              {p}
            </FilterButton>
          ))}
        </FilterGroup>
        <FilterGroup>
          {(["open", "snoozed", "done"] as StateFilter[]).map((s) => (
            <FilterButton key={s} type="button" $active={stateFilter === s} onClick={() => setStateFilter(s)}>
              {s}
            </FilterButton>
          ))}
        </FilterGroup>
        <FilterGroup>
          <FilterButton type="button" $active={unreadOnly} onClick={() => setUnreadOnly(!unreadOnly)}>
            unread
          </FilterButton>
          <FilterButton
            type="button"
            $active={sort === "oldestWaiting"}
            onClick={() => setSort(sort === "oldestWaiting" ? "newest" : "oldestWaiting")}
          >
            waiting longest
          </FilterButton>
        </FilterGroup>
        <FilterGroup>
          <FilterButton type="button" $active={false} onClick={refreshThreads}>
            refresh
          </FilterButton>
          <FilterButton
            type="button"
            $active={showAutomations}
            onClick={() => setShowAutomations((v) => !v)}
          >
            automations
          </FilterButton>
        </FilterGroup>
        <FilterGroup style={{ borderRight: "none", marginLeft: "auto", paddingRight: 0 }}>
          <SearchInput
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or message…"
            aria-label="Search threads"
          />
        </FilterGroup>
      </div>

      {showAutomations ? <UniboxAutomations communityId={communityId} /> : null}

      {error ? <Muted style={{ color: "var(--color-danger)" }}>{error}</Muted> : null}

      <Wrap>
        <Col>
          <ColHeader>
            Threads {threads.length ? `(${threads.length}${nextCursor ? "+" : ""})` : ""}
          </ColHeader>
          <div style={{ overflowY: "auto", flex: "1 1 auto" }}>
            {loading && !threads.length ? (
              <Muted>Loading…</Muted>
            ) : threads.length === 0 ? (
              <Muted>
                {search
                  ? `No threads match "${search.trim()}".`
                  : stateFilter === "open"
                  ? "Inbox zero. Inbound DMs and comments will appear here."
                  : `No ${stateFilter} threads.`}
              </Muted>
            ) : (
              threads.map((t) => (
                <ThreadItem
                  key={t.id}
                  type="button"
                  $active={t.id === selectedId}
                  onClick={() => openThread(t.id)}
                >
                  <ThreadTop>
                    <Name>{threadName(t)}</Name>
                    {t.unreadCount > 0 ? <UnreadDot /> : null}
                    <TimeLabel>
                      {t.awaitingReply && sort === "oldestWaiting"
                        ? `waiting ${relativeTime(t.lastInboundAt)}`
                        : relativeTime(t.lastMessageAt)}
                    </TimeLabel>
                  </ThreadTop>
                  <ThreadTop>
                    <Tag>{t.platform}</Tag>
                    <Tag>{t.kind}</Tag>
                    {t.leadRef ? <Tag>lead</Tag> : null}
                    {t.state === "snoozed" && t.snoozeUntil ? (
                      <Tag>until {new Date(t.snoozeUntil).toLocaleDateString()}</Tag>
                    ) : null}
                  </ThreadTop>
                  {t.lastSnippet ? <Snippet>{t.lastSnippet}</Snippet> : null}
                </ThreadItem>
              ))
            )}
            {nextCursor ? (
              <LoadMoreButton type="button" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </LoadMoreButton>
            ) : null}
          </div>
        </Col>

        <Col>
          {!detail ? (
            <Muted>Select a thread to read and reply.</Muted>
          ) : (
            <>
              <ColHeader>
                {threadName(detail)}
                <Tag>{detail.platform}</Tag>
                <Tag>{detail.kind}</Tag>
                {detail.leadRef ? <Tag>lead</Tag> : null}
                {detail.kind === "comment" && detail.postPermalink ? (
                  <PostLink href={detail.postPermalink} target="_blank" rel="noreferrer">
                    View post
                  </PostLink>
                ) : null}
                <span style={{ marginLeft: "auto", display: "inline-flex", gap: 4, alignItems: "center" }}>
                  {detail.state === "open" ? (
                    <>
                      {SNOOZE_OPTIONS.map((o) => (
                        <GhostButton
                          key={o.label}
                          type="button"
                          title={`Snooze for ${o.label}`}
                          onClick={() => changeState("snoozed", o.ms)}
                        >
                          zzz {o.label}
                        </GhostButton>
                      ))}
                      <GhostButton type="button" onClick={() => changeState("done")}>
                        Done
                      </GhostButton>
                    </>
                  ) : (
                    <GhostButton type="button" onClick={() => changeState("open")}>
                      Reopen
                    </GhostButton>
                  )}
                </span>
              </ColHeader>
              <Messages ref={messagesRef}>
                {localMessages.map((m) => {
                  const day = new Date(m.at || 0).toDateString();
                  const showSep = day !== lastDay && m.at > 0;
                  if (showSep) lastDay = day;
                  return (
                    <React.Fragment key={m.id}>
                      {showSep ? <DaySep>{day}</DaySep> : null}
                      <Bubble $out={m.direction === "out"} $pending={m.pending} $failed={m.failed}>
                        {m.text}
                      </Bubble>
                      <BubbleMeta $out={m.direction === "out"}>
                        {m.pending ? (
                          "sending…"
                        ) : m.failed ? (
                          <>
                            failed{" "}
                            <a
                              href="#retry"
                              onClick={(e) => {
                                e.preventDefault();
                                retrySend(m);
                              }}
                            >
                              retry
                            </a>
                          </>
                        ) : (
                          clockTime(m.at)
                        )}
                      </BubbleMeta>
                    </React.Fragment>
                  );
                })}
                {localMessages.length === 0 ? <Muted>No messages in this thread.</Muted> : null}
              </Messages>
              {showSnippets && snippets.length ? (
                <SnippetTray>
                  {snippets.map((s, i) => (
                    <SnippetChip key={i} type="button" onClick={() => insertSnippet(s)} title={s}>
                      {s.length > 42 ? `${s.slice(0, 42)}…` : s}
                    </SnippetChip>
                  ))}
                </SnippetTray>
              ) : null}
              <Composer>
                {detail.windowExpiresAt ? (
                  <WindowChip
                    $closed={windowClosed}
                    title="Meta allows DM replies up to 24h after the contact's last message"
                  >
                    {windowClosed ? "window closed" : `${hoursLeft(detail.windowExpiresAt)}h left`}
                  </WindowChip>
                ) : null}
                <GhostButton
                  type="button"
                  onClick={handleDraft}
                  disabled={!canReply || drafting}
                  title="Draft a reply in your voice with AI"
                >
                  {drafting ? "Drafting…" : "AI draft"}
                </GhostButton>
                {snippets.length ? (
                  <GhostButton
                    type="button"
                    onClick={() => setShowSnippets((v) => !v)}
                    title="Insert a saved snippet"
                  >
                    Snippets
                  </GhostButton>
                ) : null}
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={composerPlaceholder}
                  disabled={!canReply || sending}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
                <Button type="button" onClick={handleSend} disabled={!canReply || sending || !draft.trim()}>
                  {sending ? "Sending…" : "Send"}
                </Button>
              </Composer>
            </>
          )}
        </Col>
      </Wrap>
    </div>
  );
};

export default UniboxPanel;
